import React from 'react';
import { Box, SxProps } from '@mui/material';
import Wordmark from '@client/assets/images/nodon-logo-wordmark.svg';
import LogoIcon from '@client/assets/images/nodon-logo-icon.svg';
import { useIsProjectList } from '@client/hooks/use-router.hooks';
import { Icon } from '@ui/Icon';
import { AppBarElementId } from '../hooks/use-appbar-styles';
import { PROJECT_LOADING_STATUS_NAME } from './project-selector-toggle.constants';

/**
 * Displays either the Nodon logo or a back arrow, toggled via CSS on the
 * parent {@link AppBarElementId.ProjectSelectorContainer} hover state.
 * Navigation is handled by the parent container.
 */
export const ProjectSelectorToggle: React.FC = () => {
  const isProjectList = useIsProjectList();

  return (
    <Box
      role="status"
      aria-label={PROJECT_LOADING_STATUS_NAME}
      sx={{
        ...containerSx,
        width: isProjectList
          ? LOGO_WIDTH + LOGO_LEFT_INSET * 2
          : CONTAINER_SIZE,
        justifyContent: isProjectList ? 'flex-start' : 'center',
      }}
    >
      <Box className={AppBarElementId.ArrowBackIcon} sx={arrowSx}>
        <Icon category="mui" icon="ArrowBack" size={16} />
      </Box>
      <Box
        component="img"
        className={AppBarElementId.NodonLogo}
        alt=""
        src={isProjectList ? Wordmark : LogoIcon}
        sx={isProjectList ? wordmarkLogoSx : iconLogoSx}
      />
    </Box>
  );
};

const CONTAINER_SIZE = 24;
// The supplied icon occupies 202 × 214 units; preserve its previous 14 px width.
const LOGO_SCALE = 14 / 202;
const LOGO_WIDTH = 1043 * LOGO_SCALE;
const LOGO_HEIGHT = 214 * LOGO_SCALE;
const LOGO_LEFT_INSET = 5;

const wordmarkLogoSx: SxProps = {
  display: 'block',
  height: LOGO_HEIGHT,
  width: LOGO_WIDTH,
  ml: `${LOGO_LEFT_INSET}px`,
};

const iconLogoSx: SxProps = { display: 'block', width: 14, height: 15 };

const containerSx = {
  display: 'flex',
  alignItems: 'center',
  height: CONTAINER_SIZE,
  flexShrink: 0,
  opacity: 0.8,
} as const;

// Color and opacity are controlled by the parent projectSelectorContainer CSS class.
const arrowSx: SxProps = {
  width: CONTAINER_SIZE,
  height: CONTAINER_SIZE,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
